import ContractPage from "@/pages/contract-page.vue";
import CustomerCreateContractPage from "@/pages/customer-create-contract-page.vue";
import CustomerCreateUserAdminPage from "@/pages/customer-create-user-admin-page.vue";
import CustomerCreateUserPage from "@/pages/customer-create-user-page.vue";
import CustomerPage from "@/pages/customer-page.vue";
import CustomersCreatePage from "@/pages/customers-create-page.vue";
import CustomersPage from "@/pages/customers-page.vue";
import ProfilePage from "@/pages/profile-page.vue";
import PhoneServicePage from "@/pages/phone-service-page.vue";
import ReportPage from "@/pages/report-page.vue";
import RootPage from "@/pages/root-page.vue";
import ServiceOrderPage from "@/pages/service-order-page.vue";
import ServiceOrdersCreatePage from "@/pages/service-orders-create-page.vue";
import ServiceOrdersPage from "@/pages/service-orders-page.vue";
import InviteUsersPage from "@/pages/user-invitation-page.vue";
import CustomerServicePage from "./pages/customer-service-page.vue";
import UserPage from "@/pages/user-page.vue";
import { authService } from "@/services/auth-service";
import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import type { UserDTO } from "./models/dtos/user-dto";
import LoginPage from "./pages/login-page.vue";
import { contractsService } from "./services/contracts-service";
import {
  isCustomerAdminForAtLeastOneCustomer,
  isCustomerAdminForCustomer,
  isOneAdmin,
  isOneUser,
  isOneUserAdmin,
} from "./utils/roles.util";
import ErrorReportsCreatePage from "./pages/external/error-reports-create-page.vue";
import CustomerServiceCreateAdminPage from "./pages/customer-service-create-admin-page.vue";
import CustomerServiceCreateUserPage from "./pages/customer-service-create-user-page.vue";
import EntraPage from "@/pages/entra-page.vue";

type Route = {
  meta: {
    requiresAuthentication?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    isAllowedCallback?: (
      loggedInUser: UserDTO,
      params?: Record<string, any>,
    ) => Promise<boolean>;
  };
} & RouteRecordRaw;

export enum RouteName {
  Contract = "Contract",
  Customer = "Customer",
  CustomerCreateContract = "CustomerCreateContract",
  CustomerCreateUser = "CustomerCreateUser",
  CustomerCreateUserAdmin = "CustomerCreateUserAdmin",
  Customers = "Customers",
  CustomersCreate = "CustomersCreate",
  Login = "Login",
  Profile = "Profile",
  Root = "Root",
  ServiceOrder = "ServiceOrder",
  Report = "Report",
  ServiceOrders = "ServiceOrders",
  ServiceOrdersCreate = "ServiceOrdersCreate",
  User = "User",
  ErrorReportsCreate = "ErrorReportCreate",
  PhoneService = "PhoneService",
  CustomerService = "CustomerService",
  CustomerServiceCreateAdmin = "CustomerServiceCreateAdmin",
  CustomerServiceCreateUser = "CustomerServiceCreateUser",
  ErrorReportRedirect = "ErrorReportRedirect",
  Invites = "Invites",
  Entra = "Entra",
}

export const router = createRouter({
  linkActiveClass: "is-active",
  linkExactActiveClass: "is-active",
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/login",
      name: RouteName.Login,
      component: LoginPage,
      meta: { requiresAuthentication: false },
    },
    {
      path: "/invites",
      name: RouteName.Invites,
      component: InviteUsersPage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser) {
          return isOneAdmin(loggedInUser);
        },
      },
    },

    {
      path: "/",
      name: RouteName.Root,
      component: RootPage,
      props: true,
      meta: {
        requiresAuthentication: false,
      },
    },
    {
      path: "/profile",
      name: RouteName.Profile,
      component: ProfilePage,
      props: true,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: "/entra",
      name: RouteName.Entra,
      component: EntraPage,
      props: true,
      meta: {
        requiresAuthentication: true,
      },
    },
    {
      path: "/phone-service",
      name: RouteName.PhoneService,
      component: PhoneServicePage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser) {
          return (
            isOneAdmin(loggedInUser) ||
            isOneUser(loggedInUser) ||
            isOneUserAdmin(loggedInUser)
          );
        },
      },
    },

    {
      path: "/customer-service",
      name: RouteName.CustomerService,
      component: CustomerServicePage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser) {
          return isOneAdmin(loggedInUser) || isOneUserAdmin(loggedInUser);
        },
      },
    },

    {
      path: "/customer-service/:customerNumber/admin/create",
      name: RouteName.CustomerServiceCreateAdmin,
      component: CustomerServiceCreateAdminPage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser) {
          return isOneAdmin(loggedInUser);
        },
      },
    },

    {
      path: "/customer-service/:customerNumber/user/create",
      name: RouteName.CustomerServiceCreateUser,
      component: CustomerServiceCreateUserPage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser) {
          return isOneAdmin(loggedInUser) || isOneUserAdmin(loggedInUser);
        },
      },
    },

    {
      path: "/service-orders",
      name: RouteName.ServiceOrders,
      component: ServiceOrdersPage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser) {
          return !!loggedInUser;
        },
      },
    },

    {
      path: "/service-orders/:contractId/create",
      name: RouteName.ServiceOrdersCreate,
      component: ServiceOrdersCreatePage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser, params) {
          const contract = await contractsService.getContract(
            params?.contractId,
          );
          return loggedInUser && contract?.permissionToCreateNew;
        },
      },
    },

    {
      path: "/service-orders/:serviceOrderId",
      name: RouteName.ServiceOrder,
      component: ServiceOrderPage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser) {
          return !!loggedInUser;
        },
      },
    },

    {
      path: "/reports/:reportId",
      name: RouteName.Report,
      component: ReportPage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser) {
          return !!loggedInUser;
        },
      },
    },

    {
      path: "/customers",
      name: RouteName.Customers,
      component: CustomersPage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser) {
          return (
            isOneAdmin(loggedInUser) ||
            isOneUser(loggedInUser) ||
            isCustomerAdminForAtLeastOneCustomer(loggedInUser)
          );
        },
      },
    },

    {
      path: "/customers/:customerNumber",
      name: RouteName.Customer,
      component: CustomerPage,
      props: true,
      meta: {
        requiresAuthentication: true,
        isAllowedCallback(loggedInUser, params) {
          return (
            isOneAdmin(loggedInUser) ||
            isCustomerAdminForCustomer(loggedInUser, params?.customerNumber)
          );
        },
      },
    },

    {
      path: "/customers/:customerNumber/contract/create",
      name: RouteName.CustomerCreateContract,
      component: CustomerCreateContractPage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser, params) {
          return (
            isOneAdmin(loggedInUser) ||
            isCustomerAdminForCustomer(loggedInUser, params?.customerNumber)
          );
        },
      },
    },

    {
      path: "/customers/:customerNumber/user-admin/create",
      name: RouteName.CustomerCreateUserAdmin,
      component: CustomerCreateUserAdminPage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser, params) {
          return (
            isOneAdmin(loggedInUser) ||
            isCustomerAdminForCustomer(loggedInUser, params?.customerNumber)
          );
        },
      },
    },

    {
      path: "/customers/:customerNumber/user/create",
      name: RouteName.CustomerCreateUser,
      component: CustomerCreateUserPage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser, params) {
          return (
            isOneAdmin(loggedInUser) ||
            isCustomerAdminForCustomer(loggedInUser, params?.customerNumber)
          );
        },
      },
    },

    {
      path: "/customers/create",
      name: RouteName.CustomersCreate,
      component: CustomersCreatePage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser) {
          return isOneAdmin(loggedInUser);
        },
      },
    },

    {
      path: "/customers/:customerNumber/users/:userId",
      name: RouteName.User,
      component: UserPage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser, params) {
          return (
            isOneAdmin(loggedInUser) ||
            isCustomerAdminForCustomer(loggedInUser, params?.customerNumber)
          );
        },
      },
    },

    {
      path: "/contracts/:contractId",
      name: RouteName.Contract,
      component: ContractPage,
      props: true,
      meta: {
        requiresAuthentication: true,
        async isAllowedCallback(loggedInUser) {
          return (
            isOneAdmin(loggedInUser) ||
            isCustomerAdminForAtLeastOneCustomer(loggedInUser)
          );
        },
      },
    },

    {
      path: "/error-reports/:contractId/create",
      name: RouteName.ErrorReportsCreate,
      component: ErrorReportsCreatePage,
      props: true,
      meta: {
        requiresAuthentication: false,
      },
    },
  ] as Route[],
});

router.beforeEach(async (to, from, next) => {
  const { meta, params } = to;
  const typedMeta = meta as Route["meta"];

  const loggedInUser = await authService.loadLoggedInUser();
  const requiresAuthentication = typedMeta.requiresAuthentication;
  const authenticated = authService.authenticated;
  const isAllowed =
    typedMeta?.isAllowedCallback && loggedInUser
      ? await typedMeta.isAllowedCallback(loggedInUser, params)
      : true;

  if (requiresAuthentication === false) return next();

  if (authenticated) {
    if (to.name === RouteName.Login) return next({ name: RouteName.Root });
    if (isAllowed) return next();
    if (!isAllowed) return next(from);
  }

  return next({ name: RouteName.Login });
});
